/**
 * Plan 07 — skeletal prototype: the SQUAD as per-soldier skinned meshes (High tier).
 *
 * Why not thin instances: a thin-instanced mesh renders ONE skeleton pose for every instance
 * (single draw, shared vertex transform), and per-instance skeletons do not exist on that path.
 * Walk phase, fire onset and death age are all PER-SOLDIER, so each soldier gets its own mesh
 * (sharing the source geometry) and its own skeleton. At the High cap that is 48 skinned draws in
 * the main pass (+48 in the one shadow cascade) — measured against the tier budgets by the probe
 * (draws worst case ≈ 160 < 250; tris unchanged vs the thin crowd, ≤ 250 k).
 *
 * Purity contract (P5.3, generalized to bones): the per-soldier pose and placement are PURE
 * functions of the current SimState snapshot — no render-layer animation state, no dt, no clock.
 * Death/fire timing comes from sim telemetry (losses log, fireCooldown saw-tooth), so paused
 * frames freeze the crowd exactly and `__jreb1dResume` replays reproduce byte for byte.
 *
 * The root transform (position/yaw/pitch/roll/scale) is the SAME placement math the procedural
 * Squad uses (formation, bob, roll, recoil brace, scale jitter) — the bones add the articulated
 * layer (walk cycle, muzzle rise, fall) on top. Low/Medium tiers keep the procedural thin crowd.
 */
import { Matrix, Mesh, MeshBuilder, Quaternion, Vector3 } from '@babylonjs/core/pure';
import type { Camera, PBRMaterial, Scene } from '@babylonjs/core/pure';
import { tintCharacterMaterial, type RenderMaterials } from './scene';
import {
  BLOB_AUTHORED_GROUND,
  buildShadowTexture,
  buildSoldier,
  crowdPose,
  crowdSlot,
  framingSlots,
  SQUAD_BASE_Y,
  slotTint,
  stableHash,
  toWorldX,
  type MotionLevel,
} from './actors';
import { SoldierWeaponVariants, SOLDIER_MUZZLES, weaponTier } from './soldierWeapons';
import { SOLDIER_HEAD_Y, SOLDIER_WAIST_Y } from './soldierAtlas';
import { RIG_SOLDIER, SOLDIER_PART_BONE } from './anim/rigs';
import {
  compileClip,
  createPose,
  SOLDIER_CLIP_DEATH,
  SOLDIER_CLIP_FIRE,
  SOLDIER_CLIP_WALK,
  SOLDIER_CORPSE_FADE,
  SOLDIER_CORPSE_HOLD,
  soldierPoseInto,
  type Pose,
} from './anim/clips';
import { applyPose, bindRigidSkin, buildSkeleton, type SkeletonHandle } from './anim/skeletal';
import type { SimState } from '../sim/sim';

/**
 * Corpse sink (m): 0 until the last SOLDIER_CORPSE_FADE seconds of the hold, then smoothly under the
 * deck (the deck is opaque, so the body reads as slipping away). Pure in the corpse's age.
 */
export function corpseSink(age: number): number {
  const u = Math.min(1, Math.max(0, (age - (SOLDIER_CORPSE_HOLD - SOLDIER_CORPSE_FADE)) / SOLDIER_CORPSE_FADE));
  return 0.5 * u * u * (3 - 2 * u);
}

export class SkeletalSquad {
  private readonly source: Mesh;
  private readonly weapons: SoldierWeaponVariants;
  private weaponTier = -1;
  private readonly muzzleMatrix = Matrix.Identity();
  private readonly muzzlePoint = new Vector3();
  private readonly soldiers: { mesh: Mesh; handle: SkeletonHandle }[] = [];
  /** One tinted material per formation SLOT (a soldier keeps its shade whatever mesh draws it). */
  private readonly slotMaterials: PBRMaterial[] = [];
  private readonly shadow: Mesh;
  private blobEnabled = true;
  private readonly pose: Pose;
  private readonly scratch: Pose;
  private readonly scratch2: Pose;
  private readonly compiled = {
    walk: compileClip(RIG_SOLDIER, SOLDIER_CLIP_WALK),
    fire: compileClip(RIG_SOLDIER, SOLDIER_CLIP_FIRE),
    death: compileClip(RIG_SOLDIER, SOLDIER_CLIP_DEATH),
  };
  private readonly matrices: Float32Array;
  private readonly tmp = Matrix.Identity();
  private readonly scratchScale = new Vector3(1, 1, 1);
  private readonly scratchPos = new Vector3(0, 0, 0);
  private readonly scratchRot = Quaternion.Identity();
  private readonly scratchPose = crowdPose();
  private shown = 0;
  private alive = 0;

  constructor(
    scene: Scene,
    materials: RenderMaterials,
    maxInstances: number,
    private readonly level: MotionLevel,
  ) {
    const geometry = buildSoldier(scene, materials.soldier as PBRMaterial, 'soldier');
    this.source = geometry.mesh;
    // The source renders nothing (it stays the probe/geometry/caster name handle); each soldier
    // renders via its own clone sharing the geometry, with its OWN skeleton driving the shared
    // skin buffers (same rig ⇒ same bone indices ⇒ the shared matricesIndices buffer is valid).
    const sourceHandle = buildSkeleton(scene, RIG_SOLDIER, 'soldier-src');
    bindRigidSkin(this.source, sourceHandle, geometry.parts, RIG_SOLDIER, (part) => {
      const bone = SOLDIER_PART_BONE[part];
      if (!bone) throw new Error(`[skeletal-squad] part "${part}" has no bone`);
      return bone;
    });
    this.weapons = new SoldierWeaponVariants(scene, materials.soldier, geometry);
    this.source.setEnabled(false);
    for (let i = 0; i < maxInstances; i++) {
      const mesh = new Mesh(`soldier-anim-${i}`, scene, null, this.source);
      const handle = buildSkeleton(scene, RIG_SOLDIER, `soldier-${i}`);
      mesh.skeleton = handle.skeleton;
      this.slotMaterials.push(tintCharacterMaterial(materials.soldier as PBRMaterial, `soldier-tint-${i}`, slotTint(stableHash(i, 7, 2), stableHash(i, 7, 3))));
      mesh.setEnabled(false);
      // audit #14: each soldier OWNS its quaternion (mutated in place in place() below) — the old
      // per-frame `rotationQuaternion = scratchRot.clone()` allocated 48 Quaternions per frame on High.
      mesh.rotationQuaternion = Quaternion.Identity();
      this.soldiers.push({ mesh, handle });
    }
    this.pose = createPose(RIG_SOLDIER);
    this.scratch = createPose(RIG_SOLDIER);
    this.scratch2 = createPose(RIG_SOLDIER);
    this.matrices = new Float32Array(maxInstances * 16);
    this.shadow = MeshBuilder.CreateGround('squad-shadow', { width: BLOB_AUTHORED_GROUND, height: BLOB_AUTHORED_GROUND }, scene);
    this.shadow.material = buildShadowTexture(scene);
  }

  /** Names the shadow cascade must register (main.ts): the disabled source + every clone. */
  casterNames(): string[] {
    return [this.source.name, ...this.soldiers.map((s) => s.mesh.name)];
  }

  update(sim: SimState, maxInstances: number): void {
    if (sim.weaponTier !== this.weaponTier) {
      for (const soldier of this.soldiers) this.weapons.applyTo(soldier.mesh, sim.weaponTier);
      this.weaponTier = sim.weaponTier;
    }
    const cap = Math.min(maxInstances, this.soldiers.length);
    const alive = Math.min(sim.n, cap);
    // Corpses: loss slots beyond the living formation, inside the hold, inside the instance
    // budget. Insertion-ordered Set ⇒ deterministic; a regrown formation replaces the fallen.
    const corpseSlots = new Map<number, number>(); // slot → loss time (s)
    for (const loss of sim.losses) {
      if (corpseSlots.size >= cap - alive) break;
      if (loss.slot < sim.n || loss.slot >= cap) continue;
      const age = sim.t - loss.t;
      if (age < 0 || age > SOLDIER_CORPSE_HOLD) continue;
      corpseSlots.set(loss.slot, loss.t);
    }

    let drawn = 0;
    const place = (slot: number, corpseShown: number, age = 0): void => {
      // The root transform is the procedural Squad's placement + motion, from the one crowdSlot.
      const pose = crowdSlot(slot, cap, sim, this.level, this.scratchPose, corpseShown);
      this.scratchScale.set(pose.scale, pose.scale, pose.scale);
      this.scratchPos.set(pose.x, pose.y - (corpseShown > 0 ? corpseSink(age) : 0), pose.z);
      Quaternion.RotationYawPitchRollToRef(pose.yaw, pose.pitch, pose.roll, this.scratchRot);
      Matrix.ComposeToRef(this.scratchScale, this.scratchRot, this.scratchPos, this.tmp);
      this.tmp.copyToArray(this.matrices, drawn * 16);

      const soldier = this.soldiers[drawn]!;
      soldier.mesh.position.copyFrom(this.scratchPos);
      soldier.mesh.rotationQuaternion!.copyFrom(this.scratchRot); // audit #14: in-place copy, no per-frame clone
      soldier.mesh.scaling.copyFrom(this.scratchScale);
      soldierPoseInto({ sim, level: this.level, slot, hash: pose.phase, hz: pose.hz }, this.compiled, this.pose, this.scratch, this.scratch2);
      applyPose(soldier.handle, this.pose);
      soldier.mesh.material = this.slotMaterials[slot]!;
      soldier.mesh.setEnabled(true);
      drawn++;
    };

    for (let i = 0; i < alive; i++) place(i, 0);
    // A corpse renders in the slot it died in: the pre-loss layout (crowdFrom) for the latest change,
    // else the formation that existed one soldier larger (crowdFrom only remembers the latest).
    for (const [slot, lossT] of corpseSlots) place(slot, lossT === sim.crowdAt ? Math.min(sim.crowdFrom, cap) : slot + 1, sim.t - lossT);
    for (let i = drawn; i < this.soldiers.length; i++) this.soldiers[i]!.mesh.setEnabled(false);
    this.shown = drawn;
    this.alive = alive;

    this.shadow.position.x = toWorldX(sim.squadX);
    this.shadow.position.z = sim.squadZ - 0.6;
    this.shadow.setEnabled(this.blobEnabled && alive > 0);
  }

  /** Uses the same inverse-bind skin matrix and mesh transform as the rendered gun. */
  muzzlePosition(slot: number): Vector3 | null {
    if (slot < 0 || slot >= this.alive) return null;
    const { mesh, handle } = this.soldiers[slot]!;
    handle.skeleton.prepare(true);
    const index = RIG_SOLDIER.bones.findIndex(b => b.name === 'weapon');
    Matrix.FromArrayToRef(handle.skeleton.getTransformMatrices(mesh), index * 16, this.muzzleMatrix);
    Vector3.TransformCoordinatesFromFloatsToRef(...SOLDIER_MUZZLES[weaponTier(this.weaponTier)]!, this.muzzleMatrix, this.muzzlePoint);
    Vector3.TransformCoordinatesToRef(this.muzzlePoint, mesh.computeWorldMatrix(true), this.muzzlePoint);
    return this.muzzlePoint;
  }

  disableBlob(): void {
    this.blobEnabled = false;
    this.shadow.setEnabled(false);
  }

  // --- probe surface (parity with Squad; buildProbe and tools/inspect.mjs consume these) ------

  debugAnim(): { frontBobY: number; backBobY: number; method: 'skeletal'; pure: true } {
    const bob = (i: number): number => (i >= 0 && i < this.shown ? this.matrices[i * 16 + 13]! - SQUAD_BASE_Y : 0);
    return { frontBobY: bob(0), backBobY: bob(this.shown - 1), method: 'skeletal', pure: true };
  }

  debugMatrix(i: number): number[] {
    return Array.from(this.matrices.subarray(i * 16, i * 16 + 16));
  }

  debugThinInstances(): { name: string; count: number; zMin: number | null; zMax: number | null } {
    let zMin: number | null = null;
    let zMax: number | null = null;
    for (let i = 0; i < this.shown; i++) {
      const z = this.matrices[i * 16 + 14];
      zMin = zMin === null ? z : Math.min(zMin, z);
      zMax = zMax === null ? z : Math.max(zMax, z);
    }
    return { name: 'soldier', count: this.shown, zMin, zMax };
  }

  debugFraming(camera: Camera): Record<string, unknown> {
    const at = (i: number, dy: number): Vector3 | null =>
      i >= 0 && i < this.shown
        ? new Vector3(this.matrices[i * 16 + 12], this.matrices[i * 16 + 13] + dy, this.matrices[i * 16 + 14])
        : null;
    const project = (v: Vector3): Record<string, number | string> => {
      const view = Vector3.TransformCoordinates(v, camera.getViewMatrix(true));
      if (view.z <= camera.minZ) return { depth: 'behind' };
      const p = Vector3.Project(v, Matrix.Identity(), camera.getTransformationMatrix(), camera.viewport.toGlobal(1, 1));
      return { yFromTop: p.y, xFromLeft: p.x, depth: view.z };
    };
    const { front, back, edge } = framingSlots(this.matrices, this.shown, this.alive);
    return {
      frontRowHead: at(front, SOLDIER_HEAD_Y) ? project(at(front, SOLDIER_HEAD_Y)!) : null,
      backRowFeet: at(back, 0) ? project(at(back, 0)!) : null,
      edgeSoldier: at(edge, SOLDIER_WAIST_Y) ? project(at(edge, SOLDIER_WAIST_Y)!) : null,
      instanceCount: this.shown,
    };
  }

  /** Skeleton/bone facts for the probe (truthful membership, like the shadow payload). */
  debugSkeletal(): { soldiers: number; skeletons: number; bonesPerSoldier: number; rig: string } {
    return { soldiers: this.shown, skeletons: this.soldiers.length, bonesPerSoldier: RIG_SOLDIER.bones.length, rig: RIG_SOLDIER.id };
  }

  /** Test handle: one soldier’s bone LOCAL matrix (16 floats) — determinism reads. */
  debugBoneLocal(i: number, bone: number): number[] {
    return Array.from(this.soldiers[i]!.handle.bones[bone]!.getLocalMatrix().m);
  }

  /** Test handle: bone count per soldier (rig identity on the wire). */
  get debugBoneCount(): number {
    return RIG_SOLDIER.bones.length;
  }
}
